import { z } from "zod";
import { AttachmentRequirementDisplaySchema } from "@/services/shared/attachment-requirement.types";
import {
	BoundingBoxSchema,
	EditJobFileInfoSchema,
	FileDisplaySchema,
	ServiceResultSchema,
	UserInfoLightSchema,
} from "../job/job.types";
import { SubOrganizationDisplaySchema } from "../organization/organization.types";
import {
	type CreateNoteInfo,
	CreateNoteInfoSchema,
	type NoteDisplay,
	NoteDisplayArraySchema,
	NoteDisplaySchema,
	type NoteParams,
	NoteParamsSchema,
} from "../shared/note.types";

const optionalBooleanSchema = z.preprocess((val) => {
	if (val === "true") return true;
	if (val === "false") return false;
	return val;
}, z.boolean().optional());

export const AssetTypeDisplayTableSchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	statusLifecycleId: z.string().uuid().nullable().optional(),
	statusLifecycleName: z.string().nullable().optional(),
	geometryType: z.string().nullable().optional(),
	defaultType: z.boolean().optional(),
	source: z.string().nullable().optional(),
	/** Whether this type is enabled for the organization (org-scoped list) */
	enabled: optionalBooleanSchema,
	assetTypeSubOrganizations: z
		.array(SubOrganizationDisplaySchema)
		.optional()
		.default([]),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
	hasForm: z.boolean().optional(),
});

export const AssetTypesArraySchema = z.array(AssetTypeDisplayTableSchema);
/** Matches API schema AssetGeometryIcons */
export const AssetGeometryIconsSchema = z.object({
	type: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
});

export const AssetGeometryTypesArraySchema = z.array(AssetGeometryIconsSchema);
export const AssetTypeDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	geometryType: z.string().nullable().optional(),
	defaultType: z.boolean().optional(),
	/** Whether this type is enabled for the organization */
	enabled: optionalBooleanSchema,
	source: z.string().nullable().optional(),
	assetTypeSubOrganizations: z
		.array(SubOrganizationDisplaySchema)
		.optional()
		.default([]),
	statusLifecycle: z
		.object({
			id: z.string().uuid().nullable().optional(),
			name: z.string().nullable().optional(),
		})
		.nullable()
		.optional(),
	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
	createdBy: z.string().uuid().nullable().optional(),
	updatedBy: z.string().uuid().nullable().optional(),
	/** Display name supplied directly by the backend — no secondary lookup needed */
	createdByUser: z.string().nullable().optional(),
	updatedByUser: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
	attachmentRequirement: AttachmentRequirementDisplaySchema.nullable().optional(),
});

export const AssetTypesListParamsSchema = z.object({
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().uuid().optional(),
	Enabled: z.boolean().optional(),
	SearchTerm: z.string().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

export const AssetTypesAllParamsSchema = z.object({
	subOrganizationId: z.string().optional(),
	OrganizationId: z.string().optional(),
});

export const AssetDisplaySchema = z.object({
	id: z.string().uuid(),
	organizationId: z.string().uuid(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	assetNumber: z.number().int(),
	statusId: z.string().uuid(),
	statusName: z.string().nullable().optional(),
	statusCategoryName: z.string().nullable().optional(),
	statusCategoryIcon: z.string().nullable().optional(),
	statusCategoryIconColor: z.string().nullable().optional(),
	typeId: z.string().uuid(),
	typeName: z.string().nullable().optional(),
	typeIcon: z.string().nullable().optional(),
	subOrganizationId: z.string().uuid().nullable().optional(),
	createdAt: z.string().datetime().nullable().optional(),
	createdAtOffset: z.string().nullable().optional(),
	updatedAt: z.string().datetime().nullable().optional(),
	updatedAtOffset: z.string().nullable().optional(),
	location: z.unknown().nullable().optional(),
	/** GeoJSON geometry when provided separately from `location`. */
	geometry: z.unknown().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
	entityAttachmentRequirementId: z.string().uuid().nullable().optional(),
	data: z.unknown().nullable().optional(),
	formDefinition: z.unknown().optional(),
});

export const AssetDisplayWithUsersSchema = AssetDisplaySchema.extend({
	createdBy: UserInfoLightSchema.nullable().optional(),
	updatedBy: UserInfoLightSchema.nullable().optional(),
});

export const AssetParamsSchema = z.object({
	AssetTypeIds: z.array(z.string()).optional(),
	AssetStatusIds: z.array(z.string()).optional(),
	BoundingBox: BoundingBoxSchema.optional(),
	HasCoordinates: z.boolean().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	RelatedToJobId: z.string().optional(),
	RelatedToTicketId: z.string().optional(),
	RelatedToRecordId: z.string().optional(),
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	"OrderBy.Fields": z.array(z.string()).optional(),
	"OrderBy.Descending": z.boolean().optional(),
});

/** @deprecated Use AssetParamsSchema — kept as alias for existing count call sites. */
export const AssetCountParamsSchema = AssetParamsSchema;

/**
 * Asset status display schema
 * (`GET /assets/{id}/valid-statuses` and `GET /assets/{organizationId}/valid-statuses`)
 */
export const AssetStatusDisplaySchema = z.object({
	id: z.string().uuid(),
	name: z.string().nullable(),
	description: z.string().nullable(),
	isInitialStatus: z.boolean().optional(),
});

/**
 * Asset statuses array schema
 */
export const AssetStatusesArraySchema = z.array(AssetStatusDisplaySchema);

export type AssetStatusDisplay = z.infer<typeof AssetStatusDisplaySchema>;
export type GetAssetStatusesResponse = AssetStatusDisplay[];

export const EditAssetInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	statusId: z.string().uuid().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometry: z.unknown().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type EditAssetInfo = z.infer<typeof EditAssetInfoSchema>;

/**
 * OpenAPI `CreateAssetInfo` — POST /assets
 */
export const CreateAssetInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	typeId: z.string().nullable().optional(),
	latitude: z.number().nullable().optional(),
	longitude: z.number().nullable().optional(),
	geometry: z.unknown().nullable().optional(),
	geometrySource: z.string().nullable().optional(),
	gpsMethod: z.string().nullable().optional(),
	subOrganizationId: z.string().nullable().optional(),
	formData: z.record(z.string(), z.unknown()).nullable().optional(),
	statusId: z.string().nullable().optional(),
	recordingDeviceData: z.unknown().nullable().optional(),
});

export type CreateAssetInfo = z.infer<typeof CreateAssetInfoSchema>;

/** Re-export shared file display schema for asset attachments. */
export const AssetFileDisplaySchema = FileDisplaySchema;
export const AssetFileDisplayArraySchema = z.array(FileDisplaySchema);
export type AssetFileDisplay = z.infer<typeof AssetFileDisplaySchema>;

/** OpenAPI `EditAssetFileInfo` — PUT /assets/{assetId}/files/{assetFileId} */
export const EditAssetFileInfoSchema = EditJobFileInfoSchema;
export type EditAssetFileInfo = z.infer<typeof EditAssetFileInfoSchema>;

/**
 * Query filters for asset file list / count endpoints.
 */
export const AssetFileParamsSchema = z.object({
	AssetId: z.string().optional(),
	SearchTerm: z.string().optional(),
	IncludeDeleted: z.boolean().optional(),
	CreatedBefore: z.string().optional(),
	CreatedAfter: z.string().optional(),
	UpdatedBefore: z.string().optional(),
	UpdatedAfter: z.string().optional(),
	OrganizationId: z.string().optional(),
	SubOrganizationId: z.string().optional(),
	ForOrganizationUser: z.string().optional(),
	OrderBy: z
		.object({
			Fields: z.array(z.string()).optional(),
			Descending: z.boolean().optional(),
		})
		.optional(),
});

export type AssetFileParams = z.infer<typeof AssetFileParamsSchema>;

/** Re-export shared note display schema for asset notes. */
export const AssetNoteDisplaySchema = NoteDisplaySchema;
export const AssetNoteDisplayArraySchema = NoteDisplayArraySchema;
export type AssetNoteDisplay = NoteDisplay;

/** Body for create / update asset note endpoints. */
export type AssetCreateNoteInfo = CreateNoteInfo;
export { CreateNoteInfoSchema as AssetCreateNoteInfoSchema };

/** Query filters for asset note list / count endpoints. */
export const AssetNoteParamsSchema = NoteParamsSchema;
export type AssetNoteParams = NoteParams;

export interface AssetInstanceListParams {
	RelatedToJobId?: string;
	RelatedToTicketId?: string;
	RelatedToRecordId?: string;
	OrganizationId?: string;
	SubOrganizationId?: string;
	SearchTerm?: string;
	IncludeDeleted?: boolean;
	AssetTypeIds?: string[];
	AssetStatusIds?: string[];
	CreatedBefore?: string;
	CreatedAfter?: string;
	UpdatedBefore?: string;
	UpdatedAfter?: string;
	"OrderBy.Fields"?: string[];
	"OrderBy.Descending"?: boolean;
}

export const AssetsArraySchema = z.array(AssetDisplaySchema);

export const CreateAssetTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	statusLifecycleId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	geometryType: z.string().nullable().optional(),
});

export const EditAssetTypeInfoSchema = z.object({
	organizationId: z.string().nullable().optional(),
	statusLifecycleId: z.string().nullable().optional(),
	subOrganizationIds: z.array(z.string()).nullable().optional(),
	typeId: z.string().nullable().optional(),
	name: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	icon: z.string().nullable().optional(),
	geometryType: z.string().nullable().optional(),
	locationRequired: z.boolean().optional(),
});

export type AssetDisplay = z.infer<typeof AssetDisplaySchema>;
export type AssetDisplayWithUsers = z.infer<typeof AssetDisplayWithUsersSchema>;
export type AssetTypeDisplayTable = z.infer<typeof AssetTypeDisplayTableSchema>;
export type AssetType = AssetTypeDisplayTable;
export type AssetTypeDisplay = z.infer<typeof AssetTypeDisplaySchema>;
export type AssetGeometryIcons = z.infer<typeof AssetGeometryIconsSchema>;
export type AssetGeometryType = AssetGeometryIcons;
export type AssetTypesListParams = z.infer<typeof AssetTypesListParamsSchema>;
export type AssetTypesAllParams = z.infer<typeof AssetTypesAllParamsSchema>;
export type AssetParams = z.infer<typeof AssetParamsSchema>;
export type AssetCountParams = AssetParams;
export type CreateAssetTypeInfo = z.infer<typeof CreateAssetTypeInfoSchema>;
export type EditAssetTypeInfo = z.infer<typeof EditAssetTypeInfoSchema>;

export type AssetTypesQueryOptionsInput = Partial<AssetTypesListParams> & {
	organizationId?: string;
	subOrganizationId?: string;
};

export function normalizeAssetTypesQueryOptions(
	input?: AssetTypesQueryOptionsInput,
): AssetTypesListParams {
	if (!input) return {};
	const { organizationId, subOrganizationId, ...rest } = input;
	const out: AssetTypesListParams = { ...rest };
	if (organizationId !== undefined && organizationId !== "") {
		out.OrganizationId = organizationId;
	}
	if (subOrganizationId !== undefined && subOrganizationId !== "") {
		out.SubOrganizationId = subOrganizationId;
	}
	return out;
}

export { ServiceResultSchema };
export type { ServiceResult } from "../job/job.types";
